import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import PublicCouponBanner from '../../components/PublicCouponBanner';
import '../../pcos-management-style.css';
import { apiGet, apiPost } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { useNotification } from '../../context/CustomDialogContext';
import { useLeadModal } from '../../context/LeadModalContext';
import CountryPhoneInput from '../../components/CountryPhoneInput';
import CourseEnrollModal from '../../components/CourseEnrollModal';
import courseBgImage from '../../assets/images/courses/course_bg.webp';
import heroCourseImage from '../../assets/images/courses/diet-and-nutrition.webp';

/* ─── Accordion Item (Syllabus & FAQ) ─────────────────────────────────────── */
const AccordionItem = ({ num, title, subtitle, children, isActive, onToggle }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState('0px');

  useEffect(() => {
    setHeight(isActive ? `${contentRef.current ? contentRef.current.scrollHeight : 500}px` : '0px');
  }, [isActive]);

  return (
    <div className={`fy-accordion-item ${isActive ? 'active' : ''}`}>
      <button
        className="fy-accordion-btn"
        onClick={onToggle}
        aria-expanded={isActive}
        type="button"
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          {num && <span className="fy-accordion-num">{num}</span>}
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontWeight: 700 }}>{title}</span>
            {subtitle && <span className="fy-accordion-subtitle">{subtitle}</span>}
          </span>
        </span>
        <div className="fy-accordion-icon"><i className="bi bi-plus"></i></div>
      </button>
      <div className="fy-accordion-content" style={{ maxHeight: height }}>
        <div ref={contentRef} className="fy-accordion-inner">
          {children}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────────────────── */
const DietNutrition = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCountry, formatPrice } = useCountry();
  const { showAlert } = useNotification();
  const { openModal } = useLeadModal();

  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);

  const courseTitle = "Diet and Nutrition Course";

  const getLogoUrl = () => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
    return `${apiBase}/logo.php`;
  };

  const [activeModule, setActiveModule] = useState(0);
  const [activeFaq, setActiveFaq] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    father_name: '',
    address: '',
    countryCode: '+91',
    phone: '',
    email: '',
    batch_id: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [dbCourse, setDbCourse] = useState(null);
  const [demoInfo, setDemoInfo] = useState(null);
  const [upcomingBatches, setUpcomingBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  /* Fetch active coupons */
  useEffect(() => {
    async function fetchCoupons() {
      try {
        let couponsRes = await apiGet('/public-banner-coupons.php');
        if (!couponsRes || (Array.isArray(couponsRes) && couponsRes.length === 0) || (couponsRes.data && couponsRes.data.length === 0)) {
          couponsRes = await apiGet('/public-coupons.php');
        }
        let list = [];
        if (Array.isArray(couponsRes)) {
          list = couponsRes;
        } else if (couponsRes && Array.isArray(couponsRes.data)) {
          list = couponsRes.data;
        } else if (couponsRes && Array.isArray(couponsRes.list)) {
          list = couponsRes.list;
        }
        setActiveCoupons(list);
      } catch (err) {
        console.error('Failed to fetch coupons:', err);
      }
    }
    fetchCoupons();
  }, []);

  /* 1-second interval for real-time coupon countdown */
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const parseDateSafe = (dateStr) => {
    if (!dateStr) return null;
    let cleanStr = String(dateStr).trim();
    if (!cleanStr) return null;

    let d = new Date(cleanStr);
    if (!isNaN(d.getTime())) return d;

    cleanStr = cleanStr.replace('T', ' ').replace(/-/g, '/');
    d = new Date(cleanStr);
    if (!isNaN(d.getTime())) return d;

    d = new Date(cleanStr.replace(/ /g, 'T'));
    if (!isNaN(d.getTime())) return d;

    return null;
  };

  const getBannerCouponForDietNutrition = () => {
    if (!dbCourse) return null;

    const match = activeCoupons.find(
      (c) => Number(c.course_id) === Number(dbCourse.id) || Number(c.course_id) === 0
    );
    if (!match) return null;

    if (match.status && match.status !== 'active') return null;

    const isShowOnPages = match.show_on_pages === undefined || match.show_on_pages === null || match.show_on_pages === 1 || match.show_on_pages === '1' || match.show_on_pages === true;
    if (!isShowOnPages) return null;

    const now = currentTime.getTime();

    if (match.banner_expiry) {
      const expDate = parseDateSafe(match.banner_expiry);
      if (expDate && expDate.getTime() - now <= 0) return null;
    }
    if (match.banner_start) {
      const startDate = parseDateSafe(match.banner_start);
      if (startDate && now < startDate.getTime()) return null;
    }
    if (match.expiry_date) {
      const expDate = parseDateSafe(match.expiry_date);
      if (expDate && expDate.getTime() - now <= 0) return null;
    }

    return match;
  };

  const formatCountdown = (expiryDateStr) => {
    const expDate = parseDateSafe(expiryDateStr);
    if (!expDate) return '';

    const difference = expDate.getTime() - currentTime.getTime();
    if (difference <= 0) return 'Expired';

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    const parts = [];
    if (days > 0) {
      parts.push(`${days} day${days > 1 ? 's' : ''}`);
    }
    parts.push(`${hours} hr`);
    parts.push(`${minutes} min`);
    parts.push(`${seconds} sec`);

    return parts.join(' ');
  };

  /* Scoped CSS class for design system */
  useLayoutEffect(() => {
    document.body.classList.add('pcos-page');
    return () => { document.body.classList.remove('pcos-page'); };
  }, []);

  /* Load Diet & Nutrition course data & batches & demo availability */
  useEffect(() => {
    async function loadData() {
      try {
        const coursesData = await apiGet('/public-courses.php');
        let currentCourseId = 0;
        let targetTitle = courseTitle;
        if (coursesData && Array.isArray(coursesData)) {
          let match = coursesData.find(
            c => c.title && c.title.toLowerCase().trim() === courseTitle.toLowerCase().trim()
          );
          if (!match) {
            match = coursesData.find(
              c => (c.slug && c.slug.toLowerCase().trim() === 'diet-and-nutrition-course-online') ||
                   (c.course_slug && c.course_slug.toLowerCase().trim() === 'diet-and-nutrition-course-online') ||
                   (c.title && c.title.toLowerCase().includes('diet')) ||
                   (c.title && c.title.toLowerCase().includes('nutrition'))
            );
          }
          if (match) {
            setDbCourse(match);
            currentCourseId = match.id;
            if (match.title) {
              targetTitle = match.title;
            }
          }
        }

        // Query upcoming batches for Diet & Nutrition
        let batchesData = await apiGet(`/get-upcoming-batches.php?course_title=${encodeURIComponent(targetTitle)}`);
        
        if ((!batchesData || !Array.isArray(batchesData) || batchesData.length === 0) && targetTitle !== courseTitle) {
          const fallbackBatches = await apiGet(`/get-upcoming-batches.php?course_title=${encodeURIComponent(courseTitle)}`);
          if (fallbackBatches && Array.isArray(fallbackBatches) && fallbackBatches.length > 0) {
            batchesData = fallbackBatches;
          }
        }

        if ((!batchesData || !Array.isArray(batchesData) || batchesData.length === 0) && currentCourseId > 0) {
          const idBatches = await apiGet(`/get-upcoming-batches.php?course_id=${currentCourseId}`);
          if (idBatches && Array.isArray(idBatches) && idBatches.length > 0) {
            batchesData = idBatches;
          }
        }

        setUpcomingBatches(batchesData || []);

        try {
          if (currentCourseId > 0) {
            const demoRes = await apiGet(`/public/check-demo-course.php?course_id=${currentCourseId}&slug=diet-and-nutrition-course-online`);
            if (demoRes && demoRes.available) {
              setDemoInfo(demoRes);
            }
          }
        } catch (err) {
          console.log("No demo configuration for this course.");
        }
      } catch (err) {
        console.error('Failed to load course details & batches:', err);
      } finally {
        setLoadingBatches(false);
      }
    }
    loadData();
  }, []);

  /* Sync logged-in student data */
  useEffect(() => {
    if (user && user.role === 'student') {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        countryCode: user.country_code || '+91',
        phone: user.mobile_number || user.phone || '',
        father_name: user.father_name || '',
        address: user.address || ''
      }));
    }
  }, [user]);

  /* SEO & smooth scroll */
  useEffect(() => {
    document.title = "Online Diet and Nutrition Course | PranaFlow Academy";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content',
        "Join PranaFlow Academy's live online Diet and Nutrition Course. 30 hours, 1 month, expert-led live sessions, daily recorded lectures, batches of 25. Enroll now.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', "https://pranaflowacademy.com/courses/diet-and-nutrition-course-online");

    // Open Graph Metadata
    const ogTags = [
      { property: 'og:title', content: "Online Diet and Nutrition Course | PranaFlow Academy" },
      { property: 'og:description', content: "Join PranaFlow Academy's live online Diet and Nutrition Course. 30 hours, 1 month, expert-led live sessions, daily recorded lectures, batches of 25. Enroll now." },
      { property: 'og:type', content: "website" },
      { property: 'og:url', content: "https://pranaflowacademy.com/courses/diet-and-nutrition-course-online" },
      { property: 'og:image', content: "https://pranaflowacademy.com/images/courses/diet-and-nutrition.webp" },
      { property: 'og:site_name', content: "PranaFlow Academy" }
    ];

    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter / X Metadata
    const twitterTags = [
      { name: 'twitter:card', content: "summary_large_image" },
      { name: 'twitter:title', content: "Online Diet and Nutrition Course | PranaFlow Academy" },
      { name: 'twitter:description', content: "Join PranaFlow Academy's live online Diet and Nutrition Course. 30 hours, 1 month, expert-led live sessions, daily recorded lectures, batches of 25. Enroll now." },
      { name: 'twitter:image', content: "https://pranaflowacademy.com/images/courses/diet-and-nutrition.webp" }
    ];

    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash, location.pathname]);

  /* Schema injection */
  useEffect(() => {
    const scriptId = 'jsonld-schema-dietnutrition';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    const courseSchema = {
      "@context": "https://schema.org",
      "@type": "Course",
      "@id": "https://pranaflowacademy.com/courses/diet-and-nutrition-course-online#course",
      "name": "Diet and Nutrition Course Online",
      "description": "PranaFlow Academy's Diet and Nutrition Course Online covers nutrition science, holistic wellness, digestion, metabolism, gut health, lifestyle guidance, and Ayurvedic diet principles for wellness education.",
      "url": "https://pranaflowacademy.com/courses/diet-and-nutrition-course-online",
      "provider": {
        "@id": "https://pranaflowacademy.com/#organization"
      },
      "courseMode": "online",
      "educationalCredentialAwarded": "Certificate of Completion",
      "inLanguage": "en"
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://pranaflowacademy.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Courses",
          "item": "https://pranaflowacademy.com/courses"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Diet and Nutrition Course Online",
          "item": "https://pranaflowacademy.com/courses/diet-and-nutrition-course-online"
        }
      ]
    };

    script.textContent = JSON.stringify([courseSchema, breadcrumbSchema]);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) existingScript.remove();
    };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const displayPrice = dbCourse && parseFloat(dbCourse.fee_inr) > 0 ? formatPrice(dbCourse.fee_inr, dbCourse) : (dbCourse ? 'Fee will be updated soon' : 'Loading...');

  const handleEnrollSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { showAlert({ title: "Warning", message: "Student Name is required.", type: "warning" }); return; }
    if (!formData.email.trim()) { showAlert({ title: "Warning", message: "Email address is required.", type: "warning" }); return; }
    if (!formData.countryCode.trim()) { showAlert({ title: "Warning", message: "Country Code is required.", type: "warning" }); return; }
    if (!formData.phone.trim()) { showAlert({ title: "Warning", message: "Please enter your mobile number.", type: "warning" }); return; }

    const isInactive = dbCourse && dbCourse.status === 'inactive';

    if (isInactive) {
      setIsSubmitting(true);
      try {
        const payload = {
          name: formData.name.trim(),
          father_name: formData.father_name ? formData.father_name.trim() : null,
          mobile: formData.phone.trim(),
          country_code: formData.countryCode.trim(),
          mobile_number: formData.phone.trim(),
          country_iso_code: selectedCountry ? selectedCountry.country_code : 'IN',
          email: formData.email.trim(),
          address: formData.address ? formData.address.trim() : null,
          course_id: dbCourse ? dbCourse.id : null,
          country: selectedCountry ? selectedCountry.country_name : null,
          message: "I am interested in this course. Please contact me when new batches are available.",
          source_page: window.location.pathname
        };

        await apiPost('/save-interested-lead.php', payload);
        setIsSuccess(true);
      } catch (err) {
        showAlert({ title: "Error", message: err.message || "Failed to submit details. Please try again.", type: "error" });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!formData.father_name.trim()) { showAlert({ title: "Warning", message: "Father's / Guardian's Name is required.", type: "warning" }); return; }
    if (!formData.address.trim()) { showAlert({ title: "Warning", message: "Full Address is required.", type: "warning" }); return; }
    if (!formData.batch_id) { showAlert({ title: "Warning", message: "Please select an upcoming batch.", type: "warning" }); return; }

    const selectedBatch = upcomingBatches.find(b => Number(b.id) === Number(formData.batch_id));
    if (!selectedBatch) { showAlert({ title: "Warning", message: "Selected batch not found.", type: "warning" }); return; }

    navigate('/enroll-confirm', {
      state: {
        studentDetails: {
          name: formData.name,
          father_name: formData.father_name,
          address: formData.address,
          countryCode: formData.countryCode,
          phone: formData.phone,
          email: formData.email
        },
        courseDetails: {
          id: dbCourse ? dbCourse.id : null,
          title: courseTitle,
          duration: dbCourse ? dbCourse.duration : '30 Hours',
          fee_inr: dbCourse ? dbCourse.fee_inr : 0,
          fee_usd: dbCourse ? dbCourse.fee_usd : 0
        },
        batchDetails: {
          id: selectedBatch.id,
          name: selectedBatch.batch_name,
          timing: selectedBatch.batch_timing,
          start_date: selectedBatch.start_date
        }
      }
    });
  };

  /* 10 Modules Syllabus Data */
  const modules = [
    {
      num: '01',
      title: 'Introduction to Food & Nutrition Science',
      desc: 'Learn the fundamentals of food and nutrition, the role of a balanced diet, healthy eating habits, and how nutrition supports overall health, energy, immunity, and lifestyle wellness.',
      topics: [
        'Introduction to Food & Nutrition Science',
        'Importance & Function of a Balanced Diet',
        'Role of Nutrition in Daily Energy & Immunity',
        'Healthy Lifestyle & Food Awareness Concepts',
        'Basic Food Groups & Nutritive Functions',
        'Nutrition Principles for Overall Longevity & Wellness'
      ]
    },
    {
      num: '02',
      title: 'Macronutrients, Micronutrients, Water & Fiber',
      desc: 'Understand the major and minor nutrients required by the body, including carbohydrates, proteins, fats, vitamins, minerals, water, and dietary fiber.',
      topics: [
        'Carbohydrates — Complex vs. Simple & Glycemic Index',
        'Proteins — Amino Acids, Quality & Daily Requirements',
        'Healthy Fats — Essential Fatty Acids & Lipid Functions',
        'Vitamins & Minerals — Micronutrient Deficiency Awareness',
        'Hydration & Cellular Fluid Balance',
        'Dietary Fiber, Gut Transit & Metabolic Function',
        'Energy-Giving, Body-Building & Protective Foods'
      ]
    },
    {
      num: '03',
      title: 'Digestion, Metabolism & Gut Health',
      desc: 'Learn how the body digests, absorbs, and uses nutrients, along with the importance of gut microbiome health and energy metabolism.',
      topics: [
        'Digestive System Physiology & Enzyme Activity',
        'Nutrient Breakdown, Transport & Cellular Absorption',
        'Basal Metabolic Rate (BMR) & Energy Expenditure',
        'Gut Microbiome, Prebiotics & Probiotics',
        'Fiber, Hydration & Digestive Health Connection',
        'Addressing Common Digestion Concerns (Acidity, Bloating)',
        'Daily Food Habits for Optimal Digestion'
      ]
    },
    {
      num: '04',
      title: 'Ayurveda, Naturopathy & Holistic Nutrition',
      desc: 'Explore the traditional wisdom of Ayurveda and naturopathy and understand how holistic nutrition supports body balance and natural wellness.',
      topics: [
        'Basic Principles of Ayurvedic Nutrition & Ahara',
        'Naturopathy Concepts & Natural Body Detoxification',
        'Introduction to Healing Herbs & Spices',
        'Tridosha Concept (Vata, Pitta, Kapha) & Dietary Tuning',
        'Saptadhatu (Body Tissues) & Agni (Digestive Fire) Relation',
        'Ritucharya — Seasonal Eating & Food Modifications',
        'Agni, Ama (Toxins) & Digestive Vitality',
        'Customizing Holistic Food Choices for Body Types'
      ]
    },
    {
      num: '05',
      title: 'Diet Planning, Meal Design & Calorie Calculations',
      desc: 'Learn how to design practical and balanced diet plans using nutrition calculations, portion control, and meal planning methods.',
      topics: [
        'Basics of Practical Diet Plan Construction',
        'Meal Structuring & Meal Frequency Principles',
        'Portion Control & Plate Method Guidance',
        'BMI, BMR & Recommended Dietary Allowances (RDA)',
        'Calorie Balance & Nutrient Distribution Calculations',
        'Dietary Strategies for Weight Management (Loss & Gain)',
        'Daily Schedule-Based Real-World Meal Planning',
        'Practical Ayurvedic Meal Plan Construction'
      ]
    },
    {
      num: '06',
      title: 'Food Hygiene, Sanitation & Healthy Cooking Practices',
      desc: 'Understand the importance of food safety, hygiene, sanitation, and healthy cooking methods for preserving nutrients in daily meals.',
      topics: [
        'Food Sanitation, Cross-Contamination & Storage Rules',
        'Safe Food Handling & Temperature Controls',
        'Nutrient-Preserving Cooking Methods (Steaming, Sauteing)',
        'Healthy Home-Based Meal Recipes',
        'Ayurvedic Medicinal Cooking & Recipe Preparations',
        'Food Processing Awareness & Reading Nutrition Labels',
        'Kitchen Hygiene & Food Shelf-Life Management'
      ]
    },
    {
      num: '07',
      title: 'Sports, Yoga, Fitness & Lifestyle Nutrition',
      desc: 'Learn nutrition principles for active individuals, yoga practitioners, fitness enthusiasts, and people maintaining an active lifestyle.',
      topics: [
        'Sattvic & Pranic Food Concepts for Yoga Practitioners',
        'Macronutrient Timing for Sports & Physical Fitness',
        'Pre-Workout Fueling & Post-Workout Recovery Nutrition',
        'Hydration Strategies for Active Exercise & Heat',
        'Muscle Protein Synthesis & Muscle Recovery',
        'Energy Management & Stamina Building Through Food'
      ]
    },
    {
      num: '08',
      title: 'Therapeutic Nutrition & Disease Prevention Support',
      desc: 'Understand how targeted food choices support lifestyle improvement, disease prevention, and health management for common metabolic concerns.',
      topics: [
        'Therapeutic Nutrition Concepts & Metabolic Support',
        'Dietary Strategies for Thyroid Health',
        'Glycemic Control & Insulin Sensitivity Meal Design for Diabetes',
        'Dietary Care for Acidity, Reflux & IBS',
        'Cardiovascular Health — Lipids, Sodium & Heart-Smart Foods',
        'Dietary Guidance for Fatty Liver Management',
        'Allergy, Intolerance & Anemia Awareness',
        'Lifestyle Nutrition Strategies for Long-Term Disease Prevention'
      ]
    },
    {
      num: '09',
      title: 'Women’s Health, PCOD/PCOS & Special Diets',
      desc: 'Learn nutrition fundamentals for women’s wellness, hormonal balance support, PCOD/PCOS management, pregnancy, and special life stages.',
      topics: [
        'Hormonal Balance Through Nutrition & Whole Foods',
        'PCOD & PCOS Nutrition Strategies — Insulin & Androgens',
        'Nutritional Support During Menstrual Cycle Phases',
        'Pre-Conception & Pregnancy Nutrition Awareness',
        'Postnatal Recovery & Lactation Support Fundamentals',
        'Lifestyle & Food Habits for Women’s Endocrine Health'
      ]
    },
    {
      num: '10',
      title: 'Psychology of Eating, Practical Assessment & Certification',
      desc: 'Understand mindful eating, food behavior change, client guidance basics, and complete practical diet plan assignments before certification.',
      topics: [
        'Psychology of Food, Cravings & Emotional Eating',
        'Mindful Eating Practices & Relationship with Food',
        'Behavior Change Communication & Habits Building',
        'Client Interaction & Basic Nutrition Counseling Skills',
        'Practical Diet Plan Case Study Assignment',
        'Live Q&A, Case Review & Practical Assessment',
        'Earning Your PranaFlow Academy Diet & Nutrition Certification'
      ]
    }
  ];

  /* 6 Core Pillars */
  const pillars = [
    {
      num: '01',
      title: 'Understand Food & Nutrition',
      tags: ['Macronutrients', 'Micronutrients', 'Hydration & Fiber'],
      desc: 'Master the building blocks of nutrition, nutrient functions, and how food creates cellular energy.'
    },
    {
      num: '02',
      title: 'Body Digestion & Gut Health',
      tags: ['Digestive Enzymes', 'Gut Microbiome', 'Metabolic Rate'],
      desc: 'Explore digestion, nutrient absorption, gut health, BMR calculations, and metabolic vitality.'
    },
    {
      num: '03',
      title: 'Ayurveda & Naturopathy',
      tags: ['Tridosha Concept', 'Agni & Ama', 'Seasonal Eating'],
      desc: 'Combine modern food science with Ayurvedic wisdom, doshas, seasonal eating, and healing herbs.'
    },
    {
      num: '04',
      title: 'Practical Diet Planning',
      tags: ['Meal Calculations', 'Portion Control', 'BMR & RDA'],
      desc: 'Learn practical meal planning, calorie distribution, portion design, and custom diet creation.'
    },
    {
      num: '05',
      title: 'Therapeutic & Women’s Health',
      tags: ['PCOS/PCOD Care', 'Metabolic Health', 'Special Diets'],
      desc: 'Understand dietary support for hormonal balance, PCOS/PCOD, thyroid, diabetes, and heart wellness.'
    },
    {
      num: '06',
      title: 'Eating Psychology & Certification',
      tags: ['Mindful Eating', 'Client Guidance', 'Practical Assessment'],
      desc: 'Address emotional eating, build sustainable food habits, complete case studies, and earn your certification.'
    }
  ];

  /* Common Nutrition Concerns */
  const concernPoints = [
    { title: "Confusing diet trends & myths", desc: "Overwhelmed by keto, zero-carb, intermitting fasting, and contradictory online advice" },
    { title: "Weight loss & rebound cycle", desc: "Repeatedly dieting and regaining weight without building sustainable habits" },
    { title: "Energy crashes & cravings", desc: "Mid-afternoon fatigue, sugar cravings, and unpredictable daily energy" },
    { title: "Bloating & digestive discomfort", desc: "Frequent acidity, gas, sluggish digestion, or gut sensitivity" },
    { title: "Hormonal & PCOS nutrition confusion", desc: "Uncertain about how food affects cycles, insulin sensitivity, and PCOD/PCOS" },
    { title: "Unhealthy convenience eating", desc: "Relying on processed foods due to busy work, college, or family schedules" },
    { title: "Meal planning paralysis", desc: "Not knowing how to structure balanced Indian meals for the family" },
    { title: "Fear of carbohydrates & staples", desc: "Thinking rice, roti, or whole fruits must be completely eliminated" },
    { title: "Nutritional gaps & deficiencies", desc: "Low iron, vitamin D, B12, or protein due to unplanned restrictive eating" },
    { title: "Lack of scientific food knowledge", desc: "Wanting to understand macronutrients, BMR, and portion control clearly" },
    { title: "Difficulty guiding clients or students", desc: "Fitness or yoga professionals wanting credible, safe nutrition guidance skills" },
    { title: "Lack of structured learning", desc: "Struggling to connect random health tips into a clear, usable nutrition system" }
  ];

  const highlights = [
    "30 Hours of Intensive Live Online Instruction",
    "Completed in 1 Month (~1 Hour per Day)",
    "Master 10 Structured Educational Modules",
    "Modern Nutrition Science + Ayurvedic Principles",
    "Macronutrient, Micronutrient & BMR Calculations",
    "Digestion, Gut Health & Metabolic Science",
    "Practical Diet Planning & Indian Meal Design",
    "Women’s Health & PCOS/PCOD Nutrition Education",
    "Interactive Live Classes with Specialist Educators",
    "PranaFlow Student Dashboard Portal Access",
    "Same-Day Class Recording Access for Catch-Up",
    "Small Interactive Batches (Max 25 Students)",
    "Practical Case Study Assessment & Feedback",
    "PranaFlow Academy Diet and Nutrition Certificate",
    "Flexible Full Fee & Installment Payment Options"
  ];

  const bonuses = [
    "Macronutrient Guide",
    "10-Module Study Manual",
    "Indian Meal Planning Guide",
    "Calorie & Portion Chart",
    "Ayurvedic Dosha Food List",
    "Diet Plan Templates",
    "PranaFlow Certificate"
  ];

  const myths = [
    { m: '"Carbohydrates make you gain weight."', f: 'Complex carbohydrates (whole grains, pulses, fruits) provide essential energy and fiber. Weight management depends on overall calorie balance and nutrient quality, not eliminating carbs.' },
    { m: '"You must follow a strict named diet to be healthy."', f: 'Sustainable health comes from balanced whole foods, adequate protein, fiber, and portion awareness tailored to your lifestyle — not extreme fad diets.' },
    { m: '"Supplements replace real food."', f: 'Supplements fill specific nutritional gaps when needed. Whole foods provide complex synergistic nutrients, fiber, and antioxidants that no pill can replace.' },
    { m: '"Fruit should be avoided because of sugar."', f: 'Whole fruits contain natural fructose bound with fiber, vitamins, and minerals. They support metabolic health and do not act like refined table sugar.' },
    { m: '"Eating late at night automatically turns food into fat."', f: 'Total daily energy intake and meal quality determine body composition. Circadian habits matter, but late eating alone is not the sole cause of weight gain.' },
    { m: '"Anyone can give medical diet therapy without qualification."', f: 'General nutrition education focuses on wellness habits. Medical diet therapy for acute disease requires licensed clinical dietitians and medical supervision.' }
  ];

  const diffPoints = [
    { title: "Structured 1-Month Timeline", desc: "A focused 30-hour curriculum delivered over 1 month with ~1 hour daily live training." },
    { title: "Scientific + Ayurvedic Integration", desc: "Combines modern nutrition science, BMR/RDA calculations, and Ayurvedic Ahara wisdom." },
    { title: "Live Interactive Classes", desc: "Real-time teaching, practical meal calculations, and live Q&A with experienced educators." },
    { title: "PranaFlow Student Dashboard", desc: "All course recordings, schedule, and study materials organized neatly in your personal portal." },
    { title: "Same-Day Recording Access", desc: "Missed a live session due to work or travel? Catch up on the recorded lecture uploaded the same day." },
    { title: "Revision During Course", desc: "Revisit previous lecture videos anytime during your enrollment to reinforce learning." },
    { title: "Small Batch Attention", desc: "Limited to maximum 25 students per batch for direct instructor feedback and personal attention." },
    { title: "Practical Case Study Certification", desc: "Includes practical meal planning assignments and awards the PranaFlow Academy Certificate upon completion." }
  ];

  const faqs = [
    { q: "Do I need any prior science background to join this course?", a: "No. This course is designed to be beginner-friendly and accessible even if you are completely new to nutrition study." },
    { q: "How long is the course?", a: "The course includes 30 hours of learning over approximately one month (~1 hour per day)." },
    { q: "What happens if I miss a live class?", a: "Every live class is recorded and uploaded to your PranaFlow Student Dashboard on the same day so you can catch up easily." },
    { q: "Can I rewatch lectures I have already attended?", a: "Yes. All recorded sessions remain available on your dashboard for revision during your access period." },
    { q: "What certificate do I receive?", a: "You receive a professional certificate directly from PranaFlow Academy confirming completion of the Diet & Nutrition Course upon successfully completing the required classes, assignments, learning activities, and final assessment." },
    { q: "How many students are in each batch?", a: "Each batch is strictly limited to a maximum of 25 students for personalized guidance." },
    { q: "Can I pay in installments?", a: "Yes, installment payment options are available according to configured course plans." },
    { q: "Does this certificate make me a medical dietitian?", a: "No. This course provides comprehensive wellness nutrition education. Clinical medical nutrition therapy requires formal university degrees and clinical licensing." },
    { q: "How does this course complement yoga or fitness teaching?", a: "It helps yoga teachers and fitness coaches understand food, energy, recovery, and holistic wellbeing clearly, making their overall wellness guidance more complete." },
    { q: "Will I receive a Student Dashboard?", a: "Yes, after successful enrollment according to the standard PranaFlow Academy LMS account flow." }
  ];

  return (
    <div className="pcos-page public-site">
      <PublicHeader />

      <main className="main">
        <div className="fy-header-spacer"></div>

        {/* 1. HERO SECTION */}
        <section id="hero" className="fy-section">
          <div className="fy-hero-container">
            <div className="fy-hero-wrapper">
              <div className="row align-items-center gy-4">
                <div className="col-lg-6">
                  <span className="fy-hero-pill">
                    <i className="bi bi-star-fill text-warning me-1"></i> Live Online Certificate Course
                  </span>
                  <h1 className="fy-hero-title">Online Diet and Nutrition Course</h1>
                  <div className="fy-hero-subhead">Understand Food. Master Nutrition Fundamentals. Build Knowledge You Can Use Every Day.</div>

                  <p className="fy-hero-para">
                    Transform your relationship with food and health with PranaFlow Academy's Online Diet and Nutrition Course — a focused, expert-led 30-hour live training completed in just 1 month.
                  </p>

                  <p className="fy-hero-para">
                    Learn modern nutrition science, macronutrients, micronutrients, digestion, metabolism, gut health, Ayurveda, naturopathy, practical meal design, calorie calculations, women's health nutrition, and mindful eating habits — with all sessions recorded and managed through your PranaFlow Student Dashboard.
                  </p>

                  <div className="fy-btn-group">
                    <button type="button" className="fy-btn-primary" onClick={() => setIsEnrollModalOpen(true)}>
                      Enroll Now <i className="bi bi-arrow-right-circle-fill"></i>
                    </button>
                    <button type="button" className="fy-btn-secondary" onClick={() => openModal(courseTitle, dbCourse)}>
                      Enquire Now
                    </button>
                    {demoInfo && demoInfo.available && (
                      <button 
                        type="button"
                        className="fy-btn-secondary" 
                        onClick={() => navigate(`/demo-class?course=${demoInfo.courseSlug || 'diet-and-nutrition-course-online'}&course_id=${dbCourse ? dbCourse.id : ''}&demo_course_id=${demoInfo.demoCourseId}`, { state: { course: dbCourse } })}
                      >
                        {demoInfo.demoFee > 0 
                          ? `Demo Class — ₹${Number(demoInfo.demoFee)}` 
                          : 'Demo Class'}
                      </button>
                    )}
                  </div>

                  <div className="fy-trust-badges">
                    <span><i className="bi bi-clock-fill"></i> 30 Hours / 1 Month</span>
                    <span><i className="bi bi-laptop"></i> Live Online Training</span>
                    <span><i className="bi bi-people-fill"></i> Max 25 Per Batch</span>
                    <span><i className="bi bi-award-fill"></i> Academy Certificate</span>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="fy-hero-img-container">
                    <img src={heroCourseImage} alt="Online Diet and Nutrition Course" loading="eager" />
                  </div>
                </div>
              </div>

              {/* Feature Callout Strip */}
              <div className="fy-strip-container">
                <div className="fy-strip-grid">
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-clock-history"></i></div>
                    <div className="fy-strip-label">30 Hours<br />1 Month</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-camera-video-fill"></i></div>
                    <div className="fy-strip-label">Live Online<br />Training</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-journal-bookmark"></i></div>
                    <div className="fy-strip-label">10 Detailed<br />Modules</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-cup-hot-fill"></i></div>
                    <div className="fy-strip-label">Science +<br />Ayurveda</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-speedometer2"></i></div>
                    <div className="fy-strip-label">Student<br />Dashboard</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-credit-card-2-back"></i></div>
                    <div className="fy-strip-label">Installment<br />Available</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. OVERVIEW — "A comprehensive course designed for real results" */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-plan-box">
              <h2 className="fy-serif-heading mb-4 text-center text-lg-start">A comprehensive course designed for practical nutrition learning</h2>

              <div className="row gy-4 align-items-center">
                <div className="col-lg-6">
                  <p className="fy-hero-para">
                    You do not need a prior science background to begin. Whether you want to understand your own nutrition better, plan balanced meals for your family, or expand your wellness skill set as a yoga or fitness professional — this structured 1-month online course gives you clear, actionable guidance.
                  </p>
                  <p className="fy-hero-para">
                    Taught live by specialist educators, this course combines modern nutrition science, macronutrient/micronutrient functions, digestion physiology, Ayurvedic diet wisdom, calorie/BMR calculations, and practical meal planning — with daily class recordings uploaded to your personal PranaFlow Student Dashboard.
                  </p>

                  <div className="fy-disclaimer-box">
                    <i className="bi bi-info-circle-fill"></i>
                    <div>
                      This course provides general wellness nutrition education and lifestyle guidance. It does not replace individualized clinical dietetics or medical treatment by a licensed healthcare professional.
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Macronutrients, micronutrients, fiber &amp; hydration science</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Digestion physiology, gut microbiome &amp; energy metabolism</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Ayurveda Ahara, naturopathy &amp; seasonal eating (Ritucharya)</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>BMR, BMI, RDA calculations &amp; portion control design</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Sports, yoga, fitness &amp; exercise recovery nutrition</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>PranaFlow Student Dashboard with daily video access</span></div>
                  <div className="fy-benefit-pill mb-0"><i className="bi bi-check-circle-fill"></i><span>Psychology of eating, practical assessment &amp; certification</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 3. HIGHLIGHTS & BONUS — "What You Will Cover" */}
        <section id="highlights" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">What You Will Cover</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="row gy-4 align-items-stretch">
              <div className="col-lg-8">
                <div className="fy-highlights-grid">
                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Nutrition Science Foundations &amp; Food Groups</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Carbohydrates, Proteins &amp; Essential Fats</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Vitamins, Minerals, Fiber &amp; Hydration</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Digestion Physiology &amp; Nutrient Absorption</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Gut Microbiome, Prebiotics &amp; Probiotics</span></div>
                  </div>

                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Ayurvedic Nutrition &amp; Tridosha Concepts</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Naturopathy &amp; Healing Food Practices</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>BMR, Calorie Calculations &amp; Portion Control</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Practical Indian Meal Planning &amp; Design</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Food Sanitation, Storage &amp; Healthy Cooking</span></div>
                  </div>

                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Yoga, Sports &amp; Workout Recovery Nutrition</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Therapeutic Nutrition Awareness &amp; Health Support</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Women's Health &amp; PCOS/PCOD Diet Education</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Eating Psychology &amp; Mindful Food Habits</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>PranaFlow Academy Certificate of Completion</span></div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="fy-bonus-card">
                  <div className="fy-bonus-icon"><i className="bi bi-gift-fill"></i></div>
                  <div className="fy-bonus-title">INCLUDED RESOURCES</div>
                  <div className="fy-bonus-sub">Manuals, Guides &amp; Templates</div>
                  <ul className="fy-bonus-list">
                    {bonuses.map((b, i) => (
                      <li key={i}><i className="bi bi-check2-circle"></i>{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. CURRICULUM OVERVIEW — 6 Pillars & 10 Modules Accordion */}
        <section id="curriculum" className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Course Overview — 6 Core Learning Pillars</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-curriculum-grid mb-4">
              {pillars.map((p, i) => (
                <div key={i} className="fy-week-card">
                  <div className="fy-week-icon"><i className="bi bi-flower2"></i></div>
                  <span className={`fy-week-badge ${i % 2 === 0 ? 'sage' : 'blush'}`}>{p.num}</span>
                  <h3 className="fy-week-title">{p.title}</h3>
                  <ul className="fy-week-list">
                    {p.tags.map((t, j) => <li key={j}>{t}</li>)}
                  </ul>
                  <p style={{ fontSize: '0.85rem', color: 'var(--fy-text-muted)', marginTop: '10px', lineHeight: 1.5 }}>{p.desc}</p>
                </div>
              ))}
            </div>

            {/* 10-Module Syllabus Accordion */}
            <div className="fy-section-title-wrap mt-5 mb-4">
              <h3 className="fy-serif-heading" style={{ fontSize: '1.6rem' }}>Complete 10-Module Detailed Syllabus</h3>
              <p className="fy-hero-para" style={{ maxWidth: '650px', margin: '8px auto 0' }}>
                Click on any module to view the topics covered in the 30-hour live curriculum.
              </p>
            </div>

            <div className="fy-syllabus-container">
              {modules.map((m, idx) => (
                <AccordionItem
                  key={idx}
                  num={m.num}
                  title={m.title}
                  isActive={activeModule === idx}
                  onToggle={() => setActiveModule(activeModule === idx ? null : idx)}
                >
                  <p style={{ fontSize: '0.9rem', color: 'var(--fy-text-main)', marginBottom: '12px', lineHeight: 1.65 }}>
                    {m.desc}
                  </p>
                  <h5 style={{ fontWeight: 700, color: 'var(--fy-sage-dark)', fontSize: '0.92rem', marginBottom: '10px' }}>Topics Covered:</h5>
                  <ul style={{ paddingLeft: '20px', color: 'var(--fy-text-muted)', fontSize: '0.88rem', lineHeight: 1.65, margin: 0 }}>
                    {m.topics.map((top, tIdx) => (
                      <li key={tIdx} style={{ marginBottom: '6px' }}>{top}</li>
                    ))}
                  </ul>
                </AccordionItem>
              ))}
            </div>
          </div>
        </section>

        {/* 5. COURSE DETAILS / WHO IT IS FOR & PRICING CARD */}
        <section id="course-details" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="row gy-5 align-items-stretch">
              {/* Left image */}
              <div className="col-lg-4" style={{ textAlign: 'center' }}>
                <div className="fy-business-image-wrap">
                  <img src={courseBgImage} alt="Diet and Nutrition Course" className="img-fluid w-100" />
                </div>
              </div>

              {/* Center — Who it is for */}
              <div className="col-lg-4">
                <h2 className="fy-serif-heading mb-3">Does This Sound Like You?</h2>
                <div className="text-muted fw-bold mb-3" style={{ fontSize: '0.95rem' }}>Who joins this Diet &amp; Nutrition course.</div>

                <p className="fy-hero-para">
                  Students who feel confused by contradictory diet trends, want to understand how food works inside the body, plan balanced meals for their family, or expand their professional skill set in yoga, fitness, or wellness coaching.
                </p>

                <div className="d-flex flex-column gap-2 mb-3">
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>People wanting to understand their own food &amp; body better</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Parents &amp; homemakers planning healthy family meals</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Yoga teachers &amp; fitness trainers expanding wellness skills</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Wellness coaches &amp; beauty professionals adding nutrition clarity</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Beginners wanting structured, live expert guidance</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Anyone wanting a flexible 1-month online certification</span></div>
                </div>

                <div className="small text-muted fst-italic">
                  *PranaFlow Academy provides structured wellness education. This course is educational and does not provide clinical medical nutrition therapy.
                </div>
              </div>

              {/* Right — Pricing card V2 */}
              <div className="col-lg-4">
                {(() => {
                  const coupon = getBannerCouponForDietNutrition();
                  const feeInr = dbCourse ? parseFloat(dbCourse.fee_inr) : 0;
                  let formattedOriginalPrice = displayPrice;
                  let formattedDiscountedPrice = '';
                  let percentageVal = 0;

                  if (coupon && feeInr > 0) {
                    if (coupon.discount_type === 'percentage') {
                      percentageVal = Math.round(parseFloat(coupon.discount_value));
                      const discountedInr = feeInr * (1 - percentageVal / 100);
                      formattedDiscountedPrice = formatPrice(discountedInr, dbCourse);
                      formattedOriginalPrice = formatPrice(feeInr, dbCourse);
                    } else if (coupon.discount_type === 'flat' || coupon.discount_type === 'fixed') {
                      const fixedVal = parseFloat(coupon.discount_value);
                      const discountedInr = Math.max(0, feeInr - fixedVal);
                      percentageVal = Math.round((fixedVal / feeInr) * 100);
                      formattedDiscountedPrice = formatPrice(discountedInr, dbCourse);
                      formattedOriginalPrice = formatPrice(feeInr, dbCourse);
                    }
                  }

                  return (
                    <div className="fy-program-details-card-v2" id="pricing">
                      <h3 className="pcos-pd-heading">PROGRAM DETAILS</h3>
                      <div className="pcos-pd-heading-divider"></div>

                      <div className="pcos-pd-rows-wrap">
                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-calendar-event"></i>
                            <span>Duration</span>
                          </div>
                          <div className="pcos-pd-val">30 Hours / 1 Month</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-laptop"></i>
                            <span>Mode</span>
                          </div>
                          <div className="pcos-pd-val">Live Online Training</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-clock"></i>
                            <span>Timing</span>
                          </div>
                          <div className="pcos-pd-val">~1 Hour / Day</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-person-badge"></i>
                            <span>Batch Size</span>
                          </div>
                          <div className="pcos-pd-val">Max 25 Students</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-journal-check"></i>
                            <span>Curriculum</span>
                          </div>
                          <div className="pcos-pd-val">10 Modules · Science + Ayurveda</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-speedometer2"></i>
                            <span>Portal</span>
                          </div>
                          <div className="pcos-pd-val">PranaFlow Dashboard</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-award-fill"></i>
                            <span>Certificate</span>
                          </div>
                          <div className="pcos-pd-val">PranaFlow Academy Cert</div>
                        </div>
                      </div>

                      <div className="pf-course-promo-badge pf-course-promo-badge-pd">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                          <line x1="7" y1="7" x2="7.01" y2="7"></line>
                        </svg>
                        <span>Every batch: First 5 students get an extra discount on fee!</span>
                      </div>

                      {coupon ? (
                        <div className="pcos-pd-offer-box">
                          <div className="pcos-pd-offer-pill">
                            <i className="bi bi-star"></i> LIMITED TIME OFFER
                          </div>

                          <div className="pcos-pd-offer-prices-row">
                            <div className="pcos-pd-orig-price-wrap">
                              <span className="pcos-pd-metric-label">ORIGINAL FEE</span>
                              <span className="pcos-pd-orig-price">{formattedOriginalPrice}</span>
                            </div>

                            <div className="pcos-pd-disc-price-wrap">
                              <span className="pcos-pd-metric-label">DISCOUNTED FEE</span>
                              <span className="pcos-pd-disc-price">{formattedDiscountedPrice}</span>
                            </div>

                            <div className="pcos-pd-badge-wrap">
                              <div className="pcos-pd-badge-rect">
                                <span className="pcos-pd-badge-num">{percentageVal}%</span>
                                <span className="pcos-pd-badge-text">OFF</span>
                              </div>
                            </div>
                          </div>

                          <div className="pcos-pd-timer-strip">
                            <i className="bi bi-stopwatch"></i>
                            <span>
                              {formatCountdown(coupon.banner_expiry || coupon.expiry_date).includes('day') ? 'Offer ends in' : 'Offer valid today'} · {formatCountdown(coupon.banner_expiry || coupon.expiry_date)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="pcos-pd-normal-box">
                          <span className="pcos-pd-normal-label">TUITION FEE</span>
                          <span className="pcos-pd-normal-price">{loadingBatches ? 'Loading...' : displayPrice}</span>
                        </div>
                      )}

                      <button
                        type="button"
                        className="pcos-pd-enroll-btn"
                        onClick={() => setIsEnrollModalOpen(true)}
                      >
                        <span>Enroll Now &amp; Reserve Your Seat</span>
                        <div className="pcos-pd-btn-arrow-circle">
                          <i className="bi bi-arrow-right"></i>
                        </div>
                      </button>

                      <button
                        type="button"
                        className="fy-btn-secondary w-100 mt-2"
                        onClick={() => openModal(courseTitle, dbCourse)}
                        style={{
                          borderRadius: '30px',
                          padding: '12px 20px',
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        Enquire Now <i className="bi bi-chat-dots"></i>
                      </button>

                      <div className="pcos-pd-next-batch-strip">
                        <i className="bi bi-leaf"></i>
                        <span>Next batch admissions open — inquire now</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>

        {/* 6. COMMON NUTRITION CONCERNS */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Stop Jumping From One Diet Trend to Another</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '700px', margin: '12px auto 0' }}>
                We address the common nutrition confusion and diet barriers students experience when relying on internet advice.
              </p>
            </div>

            <div className="fy-pain-grid">
              {concernPoints.map((p, i) => (
                <div key={i} className="fy-pain-card">
                  <span className="fy-pain-x">✕</span>
                  <div>
                    <h4 className="fy-pain-title">{p.title}</h4>
                    <span className="fy-pain-desc">{p.desc}</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: 'var(--fy-cream-card)', border: '2px dashed var(--fy-sage-border)', borderRadius: 'var(--fy-radius-lg)', padding: '24px 32px', textAlign: 'center', marginTop: '32px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--fy-sage-primary)', marginBottom: '8px' }}>
                "Good nutrition is not about starvation or eliminating whole food groups. It is about understanding how nutrients work inside your body."
              </h3>
              <p style={{ color: 'var(--fy-text-muted)', margin: 0, fontSize: '0.95rem' }}>
                Our 1-month course introduces that structured, evidence-guided system so you can make confident food choices for life.
              </p>
            </div>
          </div>
        </section>

        {/* 7. PRANAFLOW STUDENT DASHBOARD */}
        <section className="fy-section fy-dashboard-section">
          <div className="fy-container">
            <div className="row align-items-center gy-5">
              <div className="col-lg-7">
                <span className="fy-dashboard-badge">PranaFlow LMS Student Portal</span>
                <h2 className="fy-serif-heading mb-3" style={{ color: '#ffffff', fontSize: '2rem' }}>Missed a Live Class? Your Learning Continues.</h2>
                <p className="fy-hero-para" style={{ color: '#C3DFC8' }}>
                  Work, family, or travel should never disrupt your momentum. Upon enrollment, you receive access to your personal <strong style={{ color: '#ffffff' }}>PranaFlow Student Dashboard</strong>.
                </p>

                <div className="row gy-2 mb-4" style={{ fontSize: '0.95rem', color: '#E8F5EB' }}>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Daily Live Lecture Links &amp; Timings</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Same-Day Class Video Recordings</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Missed-Class Catch-Up Portal</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Revision Access During Course</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Downloadable Meal Guides &amp; Charts</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Course Progress &amp; Assessment Portal</span></div></div>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 'var(--fy-radius-md)', padding: '20px 24px' }}>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fbbf24', margin: '0 0 6px' }}>
                    Learn Live. Catch Up When Needed. Master Every Concept.
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#C3DFC8', lineHeight: 1.6 }}>
                    Every live lecture is recorded and uploaded to your Student Dashboard on the same day so you can stay fully aligned with your batch.
                  </p>
                </div>
              </div>

              <div className="col-lg-5">
                <div className="fy-dashboard-card">
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--fy-sage-primary)', marginBottom: '16px' }}>Your Student Dashboard Includes:</h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.9rem', color: 'var(--fy-text-main)' }}>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Enrolled Diet &amp; Nutrition Course Access</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Daily Live Class Schedule &amp; Links</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>10 Structured Course Modules</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Uploaded Lecture Recordings</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Portion Charts &amp; Meal Templates</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Practical Case Study Submission</span></li>
                  </ul>
                  <button type="button" className="fy-btn-primary w-100" onClick={() => scrollTo('enrollment')}>
                    Get Access Upon Enrollment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 8. MYTH-BUSTING */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Diet &amp; Nutrition Myth-Busting</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '600px', margin: '12px auto 0' }}>
                Replacing internet food confusion with evidence-guided nutritional clarity.
              </p>
            </div>

            <div className="row gy-3">
              {myths.map((item, idx) => (
                <div key={idx} className="col-md-6 col-lg-4">
                  <div className="fy-myth-card">
                    <span className="fy-myth-badge">MYTH vs FACT</span>
                    <h4 className="fy-myth-q">{item.m}</h4>
                    <p className="fy-myth-a"><strong>Fact:</strong> {item.f}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 9. WHY THIS COURSE IS DIFFERENT */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Why Pay for a Nutrition Course When the Internet Is Free?</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-diff-grid">
              {diffPoints.map((card, idx) => (
                <div key={idx} className="fy-diff-card">
                  <h4 className="fy-diff-title">{card.title}</h4>
                  <p className="fy-diff-desc">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 10. 1-MONTH ROADMAP */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Your 1-Month Nutrition Learning Journey</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-roadmap-grid">
              <div className="fy-roadmap-card month1">
                <div>
                  <span className="fy-roadmap-badge month1">Week 1</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Nutrients &amp; Body Digestion</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Module 01 — Introduction &amp; Food Science</li>
                    <li>Module 02 — Macro, Micro, Water &amp; Fiber</li>
                    <li>Module 03 — Digestion, Gut &amp; BMR</li>
                  </ul>
                </div>
                <div style={{ background: 'var(--fy-sage-light)', border: '1px solid var(--fy-sage-border)', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: 'var(--fy-sage-dark)', fontWeight: 600 }}>
                  Goal: Master nutrient science &amp; gut physiology.
                </div>
              </div>

              <div className="fy-roadmap-card month2">
                <div>
                  <span className="fy-roadmap-badge month2">Weeks 2–3</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Ayurveda, Meal Design &amp; Fitness</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Module 04 — Ayurveda &amp; Naturopathy</li>
                    <li>Module 05 — Diet Calculations &amp; Plans</li>
                    <li>Module 06 — Food Hygiene &amp; Cooking</li>
                    <li>Module 07 — Sports, Yoga &amp; Fitness Nutrition</li>
                  </ul>
                </div>
                <div style={{ background: 'var(--fy-blush-light)', border: '1px solid var(--fy-blush-border)', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: 'var(--fy-blush-dark)', fontWeight: 600 }}>
                  Goal: Build practical meal plans &amp; fitness diets.
                </div>
              </div>

              <div className="fy-roadmap-card month3">
                <div>
                  <span className="fy-roadmap-badge month3">Week 4</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Health, Psychology &amp; Certification</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Module 08 — Therapeutic Nutrition Awareness</li>
                    <li>Module 09 — Women's Health &amp; PCOS Nutrition</li>
                    <li>Module 10 — Psychology of Eating &amp; Assessment</li>
                  </ul>
                </div>
                <div style={{ background: '#DCF5E6', border: '1px solid #A7D3B6', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: '#1A5C33', fontWeight: 600 }}>
                  Goal: Complete case study &amp; earn certification.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 11. ENROLLMENT & SEAT RESERVATION */}
        <section id="pricing-form" className="fy-section">
          <div className="fy-container text-center">
            <div id="enrollment" className="py-2">
              <div className="fy-section-title-wrap text-center mb-3">
                <h2 className="fy-serif-heading" style={{ fontSize: '2rem' }}>
                  Course Enrollment &amp; Seat Reservation
                </h2>
                <div className="fy-title-underline"></div>
              </div>

              <p style={{ fontSize: '1.02rem', color: 'var(--fy-text-muted)', maxWidth: '880px', margin: '0 auto 36px', lineHeight: 1.75 }}>
                Our Yogic Diet &amp; Nutrition Certification provides practical knowledge to design personalized wellness and meal plans. Due to our commitment to interactive learning, cohort sizes are strictly limited.
              </p>

              <div className="fy-enrollment-cta-group">
                <button
                  type="button"
                  className="fy-btn-primary py-3 px-4"
                  style={{ borderRadius: '30px', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}
                  onClick={() => setIsEnrollModalOpen(true)}
                >
                  Enroll Now &amp; Reserve Your Seat <i className="bi bi-arrow-right-circle-fill ms-1"></i>
                </button>

                <button
                  type="button"
                  className="fy-btn-secondary py-3 px-4"
                  style={{ borderRadius: '30px', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}
                  onClick={() => openModal(courseTitle, dbCourse)}
                >
                  Enquire Now <i className="bi bi-chat-dots ms-1"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* 12. WHAT YOU RECEIVE */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <span className="fy-eyebrow">EVERYTHING INCLUDED</span>
              <h2 className="fy-serif-heading">What You Receive</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '650px', margin: '12px auto 0' }}>
                Everything included in the Online Diet and Nutrition Course from the moment you enroll.
              </p>
            </div>

            <div className="fy-receive-grid">
              {highlights.map((item, idx) => (
                <div key={idx} className="fy-receive-card">
                  <div className="fy-receive-icon">
                    <i className="bi bi-check-lg"></i>
                  </div>
                  <span className="fy-receive-text">{item}</span>
                </div>
              ))}
            </div>

            <div className="fy-receive-assurance">
              <i className="bi bi-shield-check"></i>
              <span>All study materials, live class links, and dashboard access are unlocked upon registration.</span>
            </div>
          </div>
        </section>

        {/* 13. CERTIFICATE SECTION */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="row align-items-center gy-4">
              <div className="col-lg-7">
                <span className="fy-eyebrow">ACADEMY CERTIFICATION</span>
                <h2 className="fy-serif-heading mb-3">Diet &amp; Nutrition Course — Professional Certification</h2>
                <p className="fy-hero-para mb-3">
                  Your <strong>Diet &amp; Nutrition Course Certificate</strong> is issued directly by <strong>PranaFlow Academy</strong> after successful completion of the program requirements.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  The course provides structured education in nutrition fundamentals, balanced eating, meal planning, healthy lifestyle principles, food awareness, dietary habits, wellness-focused nutrition, and practical application of nutritional knowledge.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  After successfully completing the required classes, assignments, learning activities, and final assessment, you will receive a <strong>professional certificate from PranaFlow Academy confirming completion of the Diet &amp; Nutrition Course</strong>.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  The certificate demonstrates that you have completed structured education in diet and nutrition through PranaFlow Academy.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1.5rem' }}>
                  This certificate represents <strong>course completion and professional education provided by PranaFlow Academy</strong>. It does not by itself provide a medical licence, government registration, or regulated healthcare qualification such as a licensed or registered dietitian credential.
                </p>

                <div className="fy-trust-badges mt-2 mb-2" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', background: 'var(--fy-cream-card)', padding: '12px 20px', borderRadius: '30px', border: '1px solid var(--fy-sage-border)', fontSize: '0.88rem', fontWeight: '600', color: 'var(--fy-sage-dark)' }}>
                  <i className="bi bi-patch-check-fill text-success" style={{ fontSize: '1.1rem' }}></i>
                  <span>Structured Nutrition Education · Practical Learning · PranaFlow Academy Certificate</span>
                </div>
              </div>

              <div className="col-lg-5 text-center">
                <div style={{ background: 'var(--fy-cream-card)', border: '2px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-lg)', padding: '36px 28px', boxShadow: 'var(--fy-shadow-card)' }}>
                  <i className="bi bi-award-fill" style={{ fontSize: '3.8rem', color: 'var(--fy-sage-primary)' }}></i>
                  <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginTop: '16px', marginBottom: '8px' }}>
                    Diet &amp; Nutrition Course Certificate
                  </h3>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--fy-sage-primary)', marginBottom: '16px' }}>Issued Directly by PranaFlow Academy</div>
                  <p style={{ fontSize: '0.88rem', color: 'var(--fy-text-main)', lineHeight: 1.6, margin: 0 }}>
                    Awarded upon completing structured live online training, assignments, meal planning activities, and practical case study evaluation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 14. FAQ */}
        <section id="faq" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Frequently Asked Questions</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-faq-grid">
              {faqs.map((faq, idx) => (
                <div key={idx} className="fy-faq-item">
                  <button className="fy-faq-btn" aria-expanded={activeFaq === idx}
                    type="button"
                    onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}>
                    <span>{faq.q}</span>
                    <div className="fy-faq-icon"><i className="bi bi-plus"></i></div>
                  </button>
                  {activeFaq === idx && (
                    <div className="fy-faq-ans" style={{ display: 'block' }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 15. FINAL CTA BANNER */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-cta-banner">
              <div className="row gy-4 align-items-center">
                <div className="col-lg-8">
                  <h2 className="fy-cta-heading">Don't Just Follow Diet Advice. Learn to Understand Nutrition.</h2>
                  <p className="fy-cta-sub">Your Diet &amp; Nutrition Learning Journey Starts Here.</p>
                  <p style={{ fontSize: '0.95rem', color: '#C3DFC8', lineHeight: 1.7, marginBottom: '20px' }}>
                    Over the next 1 month, master macronutrients, digestion, BMR calculations, Ayurvedic food wisdom, meal planning, and therapeutic nutrition awareness with live expert guidance managed in your PranaFlow Student Dashboard.
                  </p>
                  <div className="fy-btn-group">
                    <button type="button" className="fy-btn-primary fy-cta-btn-white" onClick={() => setIsEnrollModalOpen(true)}>
                      Enroll Now <i className="bi bi-arrow-right-circle-fill"></i>
                    </button>
                    <button type="button" className="fy-btn-secondary fy-cta-btn-outline" onClick={() => openModal(courseTitle, dbCourse)}>
                      Enquire Now
                    </button>
                  </div>
                </div>
                <div className="col-lg-4 text-center">
                  <div style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 'var(--fy-radius-lg)', padding: '24px 20px' }}>
                    <div className="fy-cta-logo-wrap">
                      <img 
                        src={getLogoUrl()} 
                        alt="PranaFlow Academy" 
                        className="fy-cta-card-logo"
                        onError={(e) => { e.target.onerror = null; e.target.src = '/apple-touch-icon.png'; }}
                      />
                    </div>
                    <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#ffffff', marginBottom: '4px' }}>30 Hours</div>
                    <div style={{ fontSize: '0.85rem', color: '#C3DFC8' }}>1 Month Timeline</div>
                    <div style={{ fontSize: '0.85rem', color: '#C3DFC8' }}>10 Structured Modules</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 16. COURSE NOTICE */}
        <div className="fy-container">
          <div className="fy-page-disclaimer">
            <strong>Important Nutrition Education Notice:</strong> This course is designed for educational, wellness, and self-care purposes. Diet and nutrition principles taught in this program provide general health and lifestyle guidance and do not constitute clinical medical nutrition therapy, medical diagnosis, or medical treatment for any disease. Participants should consult their treating healthcare professional before making major dietary or lifestyle changes.
          </div>
        </div>

      </main>

      <CourseEnrollModal
        isOpen={isEnrollModalOpen}
        onClose={() => setIsEnrollModalOpen(false)}
        courseTitle={courseTitle}
        formData={formData}
        setFormData={setFormData}
        upcomingBatches={upcomingBatches}
        isSubmitting={isSubmitting}
        isSuccess={isSuccess}
        handleEnrollSubmit={handleEnrollSubmit}
      />

      <PublicFooter />
    </div>
  );
};

export default DietNutrition;
